import { Box, Button, Stack, Typography } from '@mui/material';
import { ResponsiveSideDialog } from 'common/components/ResponsiveSideDialog/ResponsiveSideDialog';
import { DialogComponentProps, useDialog } from 'common/hooks/useDialog';
import { ContentDispositionType } from 'common/hooks/useFiles';
import { useFilesDownload } from 'common/hooks/useFilesDownload';
import { format } from 'date-fns';
import { sk } from 'date-fns/locale';
import { FilterOperator, ReportType, SortDirection, useReportsQuery } from 'generated/graphql';
import { useTranslation } from 'react-i18next';

import { ReactComponent as FinoptioReportBanner } from '../../../../assets/images/finoptio-report-banner.svg';
import { FinoptioReportClientProfileDialog } from '../FinoptioReportClientProfileDialog/FinoptioReportClientProfileDialog';

const FinoptioReportIntroDialogContent: React.FC<DialogComponentProps<void>> = ({ onResolve }) => {
  const { t } = useTranslation();

  const finoptioReportClientProfileDialog = useDialog<DialogComponentProps<void>, void>(
    FinoptioReportClientProfileDialog,
    {
      instanceId: 'finoptio-report-client-profile-dialog',
      navigable: true,
    },
  );

  const { data } = useReportsQuery({
    variables: {
      filterInput: {
        arguments: [
          {
            filterCondition: {
              fieldPath: 'type',
              operator: FilterOperator.Equal,
              values: [ReportType.Finoptio],
            },
          },
        ],
      },
      sortInput: { by: [{ direction: SortDirection.Desc, fieldPath: 'creationTimestamp' }] },
    },
  });

  const latestReport = data?.reports.items?.[0];

  const { downloadFile } = useFilesDownload();

  const handleFileDownload = async () => {
    if (latestReport) {
      await onResolve();
      downloadFile(latestReport.file, ContentDispositionType.ATTACHMENT, window.open());
    }
  };

  const handleContinue = async () => {
    await onResolve();
    finoptioReportClientProfileDialog.create({});
  };

  return (
    <Box>
      <Stack alignItems="center" spacing={1}>
        <Typography color="textSecondary" variant="bodyM">
          {t('finoptioReport.introDialog.step')}
        </Typography>
        <Typography textAlign="center" variant="headlineXL">
          {t('finoptioReport.introDialog.title')}
        </Typography>
        <Typography textAlign="center" variant="bodyL">
          {t('finoptioReport.introDialog.text')}
        </Typography>
        <FinoptioReportBanner />
      </Stack>

      <Stack gap={2} mt={5}>
        {latestReport && (
          <Button color="primary" fullWidth onClick={handleFileDownload} size="large">
            <Typography color="white" variant="bodyL">
              {t('finoptioReport.introDialog.download', {
                date: format(latestReport.creationTimestamp, 'dd. MMMM', { locale: sk }),
              })}
            </Typography>
          </Button>
        )}
        <Button color="primary" fullWidth onClick={handleContinue} size="large">
          <Typography color="white" variant="bodyL">
            {latestReport ? t('finoptioReport.introDialog.update') : t('finoptioReport.introDialog.continue')}
          </Typography>
        </Button>
      </Stack>
    </Box>
  );
};

export const FinoptioReportIntroDialog: React.FC<DialogComponentProps<void>> = (props) => {
  return (
    <ResponsiveSideDialog dialogId={props.instanceId} isOpen={props.isOpen} onClose={props.onResolve}>
      <FinoptioReportIntroDialogContent {...props} />
    </ResponsiveSideDialog>
  );
};
